import { useCallback, useEffect, useMemo, useState } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import { styled } from '@mui/material/styles';
import { QButton, QModal, QModalActions, QModalContent, QModalTitle, QSelect, QTypography } from '@kw/quokka-ui';
import { useSnackbar } from 'notistack';
import { createAiReport, getAiReport, listAiReports, listEligibleAiReportKeys, pollAiReport } from '../../../services/aiApi.service';
import { AiProviderKey, AiReport, AiReportSummary } from '../../../types/ai';

interface GuardianAiReportsModalProps {
  isOpen: boolean;
  onClose: () => void;
  analysisId: string;
  accessToken: string;
}

const keyId = (key?: AiProviderKey) => key?.id || key?.keyId || '';
const reportContent = (report: AiReport | null) => report?.report ?? report?.content ?? report?.markdown ?? '';

export function GuardianAiReportsModal({ isOpen, onClose, analysisId, accessToken }: GuardianAiReportsModalProps) {
  const [keys, setKeys] = useState<AiProviderKey[]>([]);
  const [reports, setReports] = useState<AiReportSummary[]>([]);
  const [selectedKeyId, setSelectedKeyId] = useState('');
  const [selectedModel, setSelectedModel] = useState('');
  const [report, setReport] = useState<AiReport | null>(null);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [statusMessage, setStatusMessage] = useState('');
  const { enqueueSnackbar } = useSnackbar();

  const completedReports = useMemo(() => reports.filter(item => item.status === 'completed'), [reports]);

  const loadReport = useCallback(
    async (model: string) => {
      setSelectedModel(model);
      setReport(null);
      if (!model) return;
      try {
        setReport(await getAiReport(accessToken, analysisId, model));
      } catch {
        enqueueSnackbar('Failed to load AI report', { variant: 'error' });
      }
    },
    [accessToken, analysisId, enqueueSnackbar],
  );

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [availableKeys, availableReports] = await Promise.all([
        listEligibleAiReportKeys(accessToken, analysisId),
        listAiReports(accessToken, analysisId),
      ]);
      setKeys(availableKeys);
      setReports(availableReports);
      setSelectedKeyId(current => current || keyId(availableKeys[0]) || '');
      const firstCompleted = availableReports.find(item => item.status === 'completed');
      if (firstCompleted) await loadReport(firstCompleted.model);
    } catch {
      enqueueSnackbar('Failed to load Guardian AI reports', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  }, [accessToken, analysisId, enqueueSnackbar, loadReport]);

  useEffect(() => {
    if (isOpen) loadData();
  }, [isOpen, loadData]);

  const generateReport = async () => {
    const selectedKey = keys.find(key => keyId(key) === selectedKeyId);
    if (!selectedKey) return;

    setGenerating(true);
    setStatusMessage('Generating report…');
    try {
      await createAiReport(accessToken, analysisId, selectedKeyId);
      const completed = await pollAiReport(accessToken, analysisId, selectedKey.model);
      const updatedReports = await listAiReports(accessToken, analysisId);
      setReports(updatedReports);

      if (completed.status === 'failed') {
        const message = completed.error || 'Guardian AI report generation failed';
        setStatusMessage(message);
        enqueueSnackbar(message, { variant: 'error' });
      } else {
        setStatusMessage('');
        await loadReport(selectedKey.model);
        enqueueSnackbar('Guardian AI report generated', { variant: 'success' });
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Guardian AI report generation failed';
      setStatusMessage(message);
      enqueueSnackbar(message, { variant: 'error' });
    } finally {
      setGenerating(false);
    }
  };

  return (
    <QModal
      open={isOpen}
      onClose={generating ? undefined : onClose}
      maxWidth="md"
      fullWidth
      title={
        <QModalTitle onClose={onClose} closeIcon={<CloseIcon />}>
          <QTypography variant="h3Medium">Guardian AI Reports</QTypography>
        </QModalTitle>
      }
      content={
        <QModalContent>
          {!loading && keys.length === 0 ? (
            <Guidance>
              <QTypography variant="h5Bold">Add an AI provider key first</QTypography>
              <QTypography variant="bodyRegular">
                An organization administrator can add a key under Settings → AI Reports → AI Provider Keys.
              </QTypography>
            </Guidance>
          ) : (
            <>
              <Controls>
                <QSelect
                  label="AI provider key"
                  value={selectedKeyId}
                  options={keys.map(key => ({
                    value: keyId(key),
                    label: `${key.keyName || 'Unnamed key'} — ${key.model}`,
                  }))}
                  onChange={event => setSelectedKeyId(event.target.value as string)}
                  formControlProps={{ sx: { minWidth: 300 } }}
                />
                <QButton variant="filled" disabled={!selectedKeyId || generating} onClick={generateReport}>
                  {generating ? 'Generating…' : 'Generate report'}
                </QButton>
              </Controls>
              {statusMessage && <QTypography variant="bodyRegular">{statusMessage}</QTypography>}
            </>
          )}

          {completedReports.length > 0 && (
            <ReportArea>
              <QSelect
                label="Generated report"
                value={selectedModel}
                options={completedReports.map(item => ({ value: item.model, label: item.model }))}
                onChange={event => loadReport(event.target.value as string)}
                formControlProps={{ sx: { minWidth: 300, marginBottom: 2 } }}
              />
              {reportContent(report) && (
                <ReportContent>
                  <QTypography markdown variant="bodyRegular">
                    {reportContent(report)}
                  </QTypography>
                </ReportContent>
              )}
            </ReportArea>
          )}
        </QModalContent>
      }
      actions={
        <QModalActions>
          <QButton disabled={generating} onClick={onClose}>
            Close
          </QButton>
        </QModalActions>
      }
    />
  );
}

const Controls = styled('div')`
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 16px;
`;

const Guidance = styled('div')`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 24px 0;
`;

const ReportArea = styled('div')`
  margin-top: 24px;
`;

const ReportContent = styled('div')`
  max-height: 55vh;
  overflow: auto;
  padding: 16px;
  border: 1px solid ${({ theme }) => theme.palette.grey[300]};
  border-radius: 4px;
`;
